import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tag,
  CheckCircle2,
  Users,
  Building2,
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Save,
  Upload,
  Mail,
  Copy,
  ExternalLink,
  FileText,
  FileSearch,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  Settings2,
  ToggleLeft,
  Sparkles,
  Link2,
  BarChart3,
  FolderOpen,
  Globe,
  KeyRound,
  Bot,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import MCPSection from "../components/MCPSection";
import { cn } from "../lib/utils";

// Settings sections configuration
const SETTINGS_SECTIONS = [
  { id: "categories", label: "Categories", icon: "Tag", adminOnly: false },
  { id: "statuses", label: "Statuses", icon: "CheckCircle2", adminOnly: false },
  { id: "accounts", label: "Accounts", icon: "Building2", adminOnly: false },
  { id: "fxrates", label: "FX Rates", icon: "DollarSign", adminOnly: false },
  { id: "import", label: "Import Center", icon: "Upload", adminOnly: true, isLink: "/import" },
  { id: "analyzer", label: "Document Analyzer", icon: "FileSearch", adminOnly: true, isLink: "/document-analyzer" },
  { id: "email", label: "Email", icon: "Mail", adminOnly: true },
  { id: "users", label: "Users", icon: "Users", adminOnly: true },
  { id: "mcp", label: "MCP Access", icon: "Bot", adminOnly: true },
  { id: "features", label: "Features", icon: "ToggleLeft", adminOnly: true },
  { id: "currencies", label: "Currencies", icon: "Globe", adminOnly: true },
  { id: "danger", label: "Danger Zone", icon: "AlertTriangle", adminOnly: true, danger: true },
];

// Danger Zone Section Component
const DangerZoneSection = () => {
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);

  const handleClearAllTransactions = async () => {
    if (confirmText !== "DELETE ALL") {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }

    setClearing(true);
    try {
      const response = await apiService.clearAllTransactions();
      toast.success(`Deleted ${response.data.deleted.transactions} transactions`);
      setClearDialogOpen(false);
      setConfirmText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear transactions");
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6" />
          Danger Zone
        </h2>
        <p className="text-slate-500 mt-1">
          These actions are destructive and cannot be undone
        </p>
      </div>

      <Card className="border-red-200 bg-red-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-red-700">Clear All Transactions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            This will permanently delete <strong>all transactions</strong> from the database, 
            including all associated messages and import history. All document links will be 
            removed to prevent broken references.
          </p>

          <div className="flex items-start gap-3 p-3 bg-red-100 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700">
              <strong>Warning:</strong> This action cannot be undone. Make sure you have a 
              backup of your data before proceeding.
            </div>
          </div>

          <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="destructive" 
                className="bg-red-600 hover:bg-red-700"
                data-testid="clear-transactions-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Transactions
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Confirm Deletion
                </DialogTitle>
                <DialogDescription>
                  This action will permanently delete all transactions from the database.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 mb-3">
                    This will delete:
                  </p>
                  <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                    <li>All transactions</li>
                    <li>All transaction messages/comments</li>
                    <li>All import batch history</li>
                    <li>All document-transaction links</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">
                    Type <strong className="text-red-600">DELETE ALL</strong> to confirm:
                  </Label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE ALL"
                    className="border-red-200 focus:ring-red-500"
                    data-testid="confirm-delete-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setClearDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleClearAllTransactions}
                  disabled={clearing || confirmText !== "DELETE ALL"}
                  data-testid="confirm-clear-btn"
                >
                  {clearing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Transactions
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </>
  );
};

const SettingsPage = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(searchParams.get("section") || "categories");

  // Update URL when section changes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSearchParams({ section });
  };

  // Sync active section from URL on mount
  useEffect(() => {
    const section = searchParams.get("section");
    if (section && SETTINGS_SECTIONS.find(s => s.id === section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Data
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [fxRates, setFxRates] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [conversionCurrencies, setConversionCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Forms
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [statusForm, setStatusForm] = useState({
    name: "",
    description: "",
    color: "#64748b",
    order_index: 0,
    is_terminal: false,
  });
  const [accountForm, setAccountForm] = useState({
    bank: "",
    label: "",
    currency: "EUR",
    identifier: "",
    opening_balance: "",
    opening_balance_date: "",
  });
  const [fxRateForm, setFxRateForm] = useState({
    from_currency: "USD",
    to_currency: "EUR",
    rate: "",
    date: "",
  });

  // Dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryEditDialogOpen, setCategoryEditDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusEditDialogOpen, setStatusEditDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountEditDialogOpen, setAccountEditDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [fxRateDialogOpen, setFxRateDialogOpen] = useState(false);
  const [currencyDialogOpen, setCurrencyDialogOpen] = useState(false);
  const [newCurrencyForm, setNewCurrencyForm] = useState({ code: "", name: "", symbol: "" });

  const [saving, setSaving] = useState(false);

  // User creation state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "contributor",
  });

  // User edit state
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: "",
    role: "",
  });

  // User delete state
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingUserAssignments, setDeletingUserAssignments] = useState(null);
  const [checkingAssignments, setCheckingAssignments] = useState(false);

  // Password reset state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Import state
  const importFileRef = useRef(null);
  const [importUploading, setImportUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedBank, setSelectedBank] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Mailgun webhook URL
  const webhookUrl = `${window.location.origin}/api/webhooks/mailgun`;
  const [emailStatus, setEmailStatus] = useState({ mailgun_webhook_key_configured: false, emails_received: 0 });

  useEffect(() => {
    const fetchEmailStatus = async () => {
      try {
        const res = await apiService.getEmailStatus();
        setEmailStatus(res.data);
      } catch (error) {
        console.error("Failed to fetch email status:", error);
      }
    };
    if (isAdmin) {
      fetchEmailStatus();
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, statusRes, accRes, fxRes] = await Promise.all([
        apiService.getCategories(),
        apiService.getStatuses(),
        apiService.getAccounts(),
        apiService.getFXRates(),
      ]);

      setCategories(catRes.data);
      setStatuses(statusRes.data);
      setAccounts(accRes.data);
      setFxRates(fxRes.data);

      if (isAdmin) {
        const [usersRes, featuresRes, currenciesRes] = await Promise.all([
          apiService.getUsers(),
          apiService.getFeatureFlags(),
          apiService.getConversionCurrencies(),
        ]);
        setUsers(usersRes.data);
        setFeatureFlags(featuresRes.data);
        setConversionCurrencies(currenciesRes.data.currencies || []);
      }
    } catch (error) {
      console.error("Failed to fetch settings data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const handleCreateCategory = async () => {
    if (!categoryForm.name) {
      toast.error("Category name is required");
      return;
    }
    setSaving(true);
    try {
      await apiService.createCategory(categoryForm);
      toast.success("Category created");
      setCategoryDialogOpen(false);
      setCategoryForm({ name: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
    setCategoryEditDialogOpen(true);
  };

  const handleUpdateCategory = async () => {
    if (!categoryForm.name) {
      toast.error("Category name is required");
      return;
    }
    setSaving(true);
    try {
      await apiService.updateCategory(editingCategory.id, categoryForm);
      toast.success("Category updated");
      setCategoryEditDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to archive "${category.name}"?`)) {
      return;
    }
    try {
      await apiService.deleteCategory(category.id);
      toast.success("Category archived");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to archive category";
      toast.error(message);
    }
  };

  // Status handlers
  const handleCreateStatus = async () => {
    if (!statusForm.name) {
      toast.error("Status name is required");
      return;
    }
    setSaving(true);
    try {
      await apiService.createStatus(statusForm);
      toast.success("Status created");
      setStatusDialogOpen(false);
      setStatusForm({
        name: "",
        description: "",
        color: "#64748b",
        order_index: 0,
        is_terminal: false,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create status");
    } finally {
      setSaving(false);
    }
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    setStatusForm({
      name: status.name,
      description: status.description || "",
      color: status.color,
      order_index: status.order_index,
      is_terminal: status.is_terminal,
    });
    setStatusEditDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!statusForm.name) {
      toast.error("Status name is required");
      return;
    }
    setSaving(true);
    try {
      await apiService.updateStatus(editingStatus.id, statusForm);
      toast.success("Status updated");
      setStatusEditDialogOpen(false);
      setEditingStatus(null);
      setStatusForm({
        name: "",
        description: "",
        color: "#64748b",
        order_index: 0,
        is_terminal: false,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStatus = async (status) => {
    if (!window.confirm(`Are you sure you want to delete "${status.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await apiService.deleteStatus(status.id);
      toast.success("Status deleted");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete status";
      toast.error(message);
    }
  };

  // Account handlers
  const handleCreateAccount = async () => {
    if (!accountForm.bank || !accountForm.label || !accountForm.identifier) {
      toast.error("Bank, label and identifier are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...accountForm,
        opening_balance: accountForm.opening_balance ? parseFloat(accountForm.opening_balance) : null,
        opening_balance_date: accountForm.opening_balance_date || null,
      };
      await apiService.createAccount(payload);
      toast.success("Account created");
      setAccountDialogOpen(false);
      setAccountForm({ bank: "", label: "", currency: "EUR", identifier: "", opening_balance: "", opening_balance_date: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      bank: account.bank,
      label: account.label,
      currency: account.currency,
      identifier: account.identifier,
      opening_balance: account.opening_balance != null ? String(account.opening_balance) : "",
      opening_balance_date: account.opening_balance_date || "",
    });
    setAccountEditDialogOpen(true);
  };

  const handleUpdateAccount = async () => {
    if (!accountForm.bank || !accountForm.label || !accountForm.identifier) {
      toast.error("Bank, label and identifier are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...accountForm,
        opening_balance: accountForm.opening_balance ? parseFloat(accountForm.opening_balance) : null,
        opening_balance_date: accountForm.opening_balance_date || null,
      };
      await apiService.updateAccount(editingAccount.id, payload);
      toast.success("Account updated");
      setAccountEditDialogOpen(false);
      setEditingAccount(null);
      setAccountForm({ bank: "", label: "", currency: "EUR", identifier: "", opening_balance: "", opening_balance_date: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to update account");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Are you sure you want to delete "${account.label}"? This cannot be undone.`)) {
      return;
    }
    try {
      await apiService.deleteAccount(account.id);
      toast.success("Account deleted");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete account";
      toast.error(message);
    }
  };

  // FX Rate handlers
  const handleCreateFXRate = async () => {
    if (!fxRateForm.rate || !fxRateForm.date) {
      toast.error("Rate and date are required");
      return;
    }
    setSaving(true);
    try {
      await apiService.createFXRate({
        ...fxRateForm,
        rate: parseFloat(fxRateForm.rate),
      });
      toast.success("FX Rate added");
      setFxRateDialogOpen(false);
      setFxRateForm({
        from_currency: "USD",
        to_currency: "EUR",
        rate: "",
        date: "",
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add FX rate");
    } finally {
      setSaving(false);
    }
  };

  // User handlers
  const handleUpdateUser = async (userId, update) => {
    try {
      await apiService.updateUser(userId, update);
      toast.success("User updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      await apiService.createUser(userForm);
      toast.success("User created successfully");
      setUserDialogOpen(false);
      setUserForm({ name: "", email: "", password: "", role: "contributor" });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create user";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({ name: u.name, role: u.role });
    setEditUserDialogOpen(true);
  };

  const handleSaveEditUser = async () => {
    if (!editUserForm.name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await apiService.updateUser(editingUser.id, editUserForm);
      toast.success("User updated successfully");
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update user";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteUser = async (u) => {
    setDeletingUser(u);
    setDeletingUserAssignments(null);
    setCheckingAssignments(true);
    setDeleteUserDialogOpen(true);
    
    try {
      const res = await apiService.getUserAssignments(u.id);
      setDeletingUserAssignments(res.data);
    } catch (error) {
      console.error("Failed to check assignments:", error);
      setDeletingUserAssignments({ assigned_transactions: 0 });
    } finally {
      setCheckingAssignments(false);
    }
  };

  const handleConfirmDeleteUser = async () => {
    setSaving(true);
    try {
      // Use force=true if there are assignments (user confirmed via dialog)
      const hasAssignments = deletingUserAssignments?.assigned_transactions > 0;
      await apiService.deleteUser(deletingUser.id, hasAssignments);
      toast.success("User deleted successfully");
      setDeleteUserDialogOpen(false);
      setDeletingUser(null);
      setDeletingUserAssignments(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete user";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenResetPassword = (u) => {
    setResetPasswordUser(u);
    setNewPassword("");
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setResettingPassword(true);
    try {
      await apiService.resetUserPassword(resetPasswordUser.id, newPassword);
      toast.success(`Password reset for ${resetPasswordUser.name}`);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      toast.error(message);
    } finally {
      setResettingPassword(false);
    }
  };

  // Feature Flag handlers
  const handleToggleFeature = async (featureKey, currentEnabled) => {
    const newEnabled = !currentEnabled;
    
    // Optimistic update
    setFeatureFlags(prev => ({
      ...prev,
      [featureKey]: {
        ...prev[featureKey],
        enabled: newEnabled
      }
    }));
    
    try {
      await apiService.updateFeatureFlag(featureKey, newEnabled);
      toast.success(`Feature ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // Revert on error
      setFeatureFlags(prev => ({
        ...prev,
        [featureKey]: {
          ...prev[featureKey],
          enabled: currentEnabled
        }
      }));
      toast.error("Failed to update feature");
    }
  };

  // Currency Conversion handlers
  const handleToggleCurrency = async (currencyCode, currentEnabled) => {
    const newEnabled = !currentEnabled;
    
    // Optimistic update
    setConversionCurrencies(prev => 
      prev.map(c => c.code === currencyCode ? { ...c, enabled: newEnabled } : c)
    );
    
    try {
      await apiService.toggleConversionCurrency(currencyCode, newEnabled);
      toast.success(`${currencyCode} conversion ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // Revert on error
      setConversionCurrencies(prev => 
        prev.map(c => c.code === currencyCode ? { ...c, enabled: currentEnabled } : c)
      );
      toast.error("Failed to update currency setting");
    }
  };

  const handleAddCurrency = async () => {
    if (!newCurrencyForm.code || !newCurrencyForm.name) {
      toast.error("Currency code and name are required");
      return;
    }
    setSaving(true);
    try {
      await apiService.addConversionCurrency(
        newCurrencyForm.code.toUpperCase(),
        newCurrencyForm.name,
        newCurrencyForm.symbol || newCurrencyForm.code.toUpperCase()
      );
      toast.success(`Currency ${newCurrencyForm.code.toUpperCase()} added`);
      setCurrencyDialogOpen(false);
      setNewCurrencyForm({ code: "", name: "", symbol: "" });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add currency";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCurrency = async (code) => {
    try {
      await apiService.removeConversionCurrency(code);
      toast.success(`Currency ${code} removed`);
      fetchData();
    } catch (error) {
      toast.error("Failed to remove currency");
    }
  };

  // Import handlers
  const handleImportFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedBank) {
      toast.error("Please select a bank first");
      return;
    }

    setImportUploading(true);
    setPreviewData(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bank", selectedBank);

      const res = await apiService.previewImport(formData);
      setPreviewData(res.data);
      
      if (res.data.transactions?.length > 0) {
        toast.success(`Found ${res.data.transactions.length} transactions to import`);
      } else {
        toast.info("No new transactions found in file");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to parse file";
      toast.error(message);
    } finally {
      setImportUploading(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData?.batch_id) return;
    
    setConfirming(true);
    try {
      const res = await apiService.confirmImport(previewData.batch_id);
      const suggestions = res.data.category_suggestions || 0;
      if (suggestions > 0) {
        toast.success(`Imported ${res.data.imported} transactions with ${suggestions} category suggestions`);
      } else {
        toast.success(`Imported ${res.data.imported} transactions`);
      }
      setPreviewData(null);
      setSelectedBank("");
    } catch (error) {
      toast.error("Failed to confirm import");
    } finally {
      setConfirming(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Icon mapping for sidebar
  const getIcon = (iconName) => {
    const icons = {
      Tag: <Tag className="w-4 h-4" />,
      CheckCircle2: <CheckCircle2 className="w-4 h-4" />,
      Building2: <Building2 className="w-4 h-4" />,
      DollarSign: <DollarSign className="w-4 h-4" />,
      Upload: <Upload className="w-4 h-4" />,
      Mail: <Mail className="w-4 h-4" />,
      Users: <Users className="w-4 h-4" />,
      ToggleLeft: <ToggleLeft className="w-4 h-4" />,
      Globe: <Globe className="w-4 h-4" />,
      FileSearch: <FileSearch className="w-4 h-4" />,
      Bot: <Bot className="w-4 h-4" />,
      AlertTriangle: <AlertTriangle className="w-4 h-4" />,
    };
    return icons[iconName] || <Settings2 className="w-4 h-4" />;
  };

  // Filter sections based on role
  const visibleSections = SETTINGS_SECTIONS.filter(
    section => !section.adminOnly || isAdmin
  );

  const handleNavClick = (section) => {
    if (section.isLink) {
      window.location.href = section.isLink;
    } else {
      handleSectionChange(section.id);
    }
  };

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">
          Manage categories, statuses, accounts, and users
        </p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-56 flex-shrink-0">
          <nav className="space-y-1 sticky top-4">
            {visibleSections.map((section) => (
              <button
                key={section.id}
                onClick={() => handleNavClick(section)}
                data-testid={`nav-${section.id}`}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors text-left",
                  activeSection === section.id && !section.isLink
                    ? section.danger 
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : section.danger
                      ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                      : section.isLink
                        ? "text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                {getIcon(section.icon)}
                {section.label}
                {section.isLink && (
                  <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                )}
                {section.adminOnly && !section.danger && !section.isLink && (
                  <Badge variant="secondary" className="ml-auto text-xs py-0 px-1.5">
                    Admin
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
        {/* Categories Section */}
        {activeSection === "categories" && (
          <>
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Categories</CardTitle>
              <Dialog
                open={categoryDialogOpen}
                onOpenChange={setCategoryDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-category-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New Category</DialogTitle>
                    <DialogDescription>Create a new transaction category for organizing expenses.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) =>
                          setCategoryForm({ ...categoryForm, name: e.target.value })
                        }
                        placeholder="e.g., Travel"
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={categoryForm.description}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            description: e.target.value,
                          })
                        }
                        placeholder="Optional description"
                      />
                    </div>
                    <Button
                      onClick={handleCreateCategory}
                      disabled={saving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-category-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create Category
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium max-w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate block cursor-default">{cat.name}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{cat.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {cat.description || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={cat.active !== false ? "default" : "secondary"}>
                          {cat.active !== false ? "Active" : "Archived"}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCategory(cat)}
                              data-testid={`edit-category-${cat.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {cat.active !== false && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCategory(cat)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-category-${cat.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Edit Category Dialog */}
          <Dialog open={categoryEditDialogOpen} onOpenChange={setCategoryEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Category</DialogTitle>
                <DialogDescription>Update the category details.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) =>
                      setCategoryForm({ ...categoryForm, name: e.target.value })
                    }
                    placeholder="e.g., Travel"
                    data-testid="edit-category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm({
                        ...categoryForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="Optional description"
                  />
                </div>
                <Button
                  onClick={handleUpdateCategory}
                  disabled={saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="update-category-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Category
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        )}

        {/* Statuses Section */}
        {activeSection === "statuses" && (
          <>
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Statuses</CardTitle>
              {isAdmin && (
                <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="add-status-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Status
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>New Status</DialogTitle>
                      <DialogDescription>Create a workflow status for tracking transactions.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={statusForm.name}
                          onChange={(e) =>
                            setStatusForm({ ...statusForm, name: e.target.value })
                          }
                          placeholder="e.g., In Review"
                          data-testid="status-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={statusForm.color}
                            onChange={(e) =>
                              setStatusForm({ ...statusForm, color: e.target.value })
                            }
                            className="w-16 h-10 p-1"
                          />
                          <Input
                            value={statusForm.color}
                            onChange={(e) =>
                              setStatusForm({ ...statusForm, color: e.target.value })
                            }
                            placeholder="#64748b"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Order Index</Label>
                        <Input
                          type="number"
                          value={statusForm.order_index}
                          onChange={(e) =>
                            setStatusForm({
                              ...statusForm,
                              order_index: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={statusForm.is_terminal}
                          onChange={(e) =>
                            setStatusForm({
                              ...statusForm,
                              is_terminal: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <Label>Terminal Status (marks transaction as complete)</Label>
                      </div>
                      <Button
                        onClick={handleCreateStatus}
                        disabled={saving}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="save-status-btn"
                      >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create Status
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Terminal</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statuses.map((status) => (
                    <TableRow key={status.id}>
                      <TableCell>{status.order_index}</TableCell>
                      <TableCell className="font-medium">{status.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded"
                            style={{ backgroundColor: status.color }}
                          />
                          <span className="text-sm text-slate-500">
                            {status.color}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {status.is_terminal ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStatus(status)}
                              data-testid={`edit-status-${status.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStatus(status)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-status-${status.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Edit Status Dialog */}
          <Dialog open={statusEditDialogOpen} onOpenChange={setStatusEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Status</DialogTitle>
                <DialogDescription>Update the workflow status details.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={statusForm.name}
                    onChange={(e) =>
                      setStatusForm({ ...statusForm, name: e.target.value })
                    }
                    placeholder="e.g., In Review"
                    data-testid="edit-status-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={statusForm.color}
                      onChange={(e) =>
                        setStatusForm({ ...statusForm, color: e.target.value })
                      }
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={statusForm.color}
                      onChange={(e) =>
                        setStatusForm({ ...statusForm, color: e.target.value })
                      }
                      placeholder="#64748b"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Order Index</Label>
                  <Input
                    type="number"
                    value={statusForm.order_index}
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        order_index: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={statusForm.is_terminal}
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        is_terminal: e.target.checked,
                      })
                    }
                    className="rounded"
                  />
                  <Label>Terminal Status (marks transaction as complete)</Label>
                </div>
                <Button
                  onClick={handleUpdateStatus}
                  disabled={saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="update-status-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Status
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        )}

        {/* Accounts Section */}
        {activeSection === "accounts" && (
          <>
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Bank Accounts</CardTitle>
              <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-account-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Account
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New Account</DialogTitle>
                    <DialogDescription>Add a new bank account for importing statements.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Bank</Label>
                      <Select
                        value={accountForm.bank}
                        onValueChange={(v) =>
                          setAccountForm({ ...accountForm, bank: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select bank" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Wise">Wise</SelectItem>
                          <SelectItem value="ING">ING</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Label</Label>
                      <Input
                        value={accountForm.label}
                        onChange={(e) =>
                          setAccountForm({ ...accountForm, label: e.target.value })
                        }
                        placeholder="e.g., Wise EUR"
                        data-testid="account-label-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select
                        value={accountForm.currency}
                        onValueChange={(v) =>
                          setAccountForm({ ...accountForm, currency: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Identifier (IBAN or Account Number)</Label>
                      <Input
                        value={accountForm.identifier}
                        onChange={(e) =>
                          setAccountForm({
                            ...accountForm,
                            identifier: e.target.value,
                          })
                        }
                        placeholder="e.g., NL82INGB0109974816"
                      />
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Opening Balance (Optional)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={accountForm.opening_balance}
                            onChange={(e) =>
                              setAccountForm({
                                ...accountForm,
                                opening_balance: e.target.value,
                              })
                            }
                            placeholder="e.g., 10000.00"
                            data-testid="account-opening-balance"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>As of Date</Label>
                          <Input
                            type="date"
                            value={accountForm.opening_balance_date}
                            onChange={(e) =>
                              setAccountForm({
                                ...accountForm,
                                opening_balance_date: e.target.value,
                              })
                            }
                            data-testid="account-opening-date"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Set the account balance before your first imported transaction
                      </p>
                    </div>
                    <Button
                      onClick={handleCreateAccount}
                      disabled={saving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-account-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create Account
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bank</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Identifier</TableHead>
                    <TableHead>Opening Balance</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((acc) => (
                    <TableRow key={acc.id}>
                      <TableCell className="font-medium">{acc.bank}</TableCell>
                      <TableCell>{acc.label}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{acc.currency}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {acc.identifier}
                      </TableCell>
                      <TableCell>
                        {acc.opening_balance != null ? (
                          <div>
                            <span className="font-medium">
                              {acc.opening_balance.toLocaleString("en-US", { minimumFractionDigits: 2 })} {acc.currency}
                            </span>
                            {acc.opening_balance_date && (
                              <span className="text-xs text-slate-500 block">
                                as of {new Date(acc.opening_balance_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={acc.active !== false ? "default" : "secondary"}>
                          {acc.active !== false ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAccount(acc)}
                              data-testid={`edit-account-${acc.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAccount(acc)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-account-${acc.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Edit Account Dialog */}
          <Dialog open={accountEditDialogOpen} onOpenChange={setAccountEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Account</DialogTitle>
                <DialogDescription>Update the bank account details.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Bank</Label>
                  <Select
                    value={accountForm.bank}
                    onValueChange={(v) =>
                      setAccountForm({ ...accountForm, bank: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Wise">Wise</SelectItem>
                      <SelectItem value="ING">ING</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Label</Label>
                  <Input
                    value={accountForm.label}
                    onChange={(e) =>
                      setAccountForm({ ...accountForm, label: e.target.value })
                    }
                    placeholder="e.g., Wise EUR"
                    data-testid="edit-account-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={accountForm.currency}
                    onValueChange={(v) =>
                      setAccountForm({ ...accountForm, currency: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Identifier (IBAN or Account Number)</Label>
                  <Input
                    value={accountForm.identifier}
                    onChange={(e) =>
                      setAccountForm({
                        ...accountForm,
                        identifier: e.target.value,
                      })
                    }
                    placeholder="e.g., NL82INGB0109974816"
                  />
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Opening Balance</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountForm.opening_balance}
                        onChange={(e) =>
                          setAccountForm({
                            ...accountForm,
                            opening_balance: e.target.value,
                          })
                        }
                        placeholder="e.g., 10000.00"
                        data-testid="edit-account-opening-balance"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>As of Date</Label>
                      <Input
                        type="date"
                        value={accountForm.opening_balance_date}
                        onChange={(e) =>
                          setAccountForm({
                            ...accountForm,
                            opening_balance_date: e.target.value,
                          })
                        }
                        data-testid="edit-account-opening-date"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Set the account balance before your first imported transaction
                  </p>
                </div>
                <Button
                  onClick={handleUpdateAccount}
                  disabled={saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="update-account-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Account
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        )}

        {/* FX Rates Section */}
        {activeSection === "fxrates" && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>FX Rates</CardTitle>
              <Dialog open={fxRateDialogOpen} onOpenChange={setFxRateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-fxrate-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Rate
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New FX Rate</DialogTitle>
                    <DialogDescription>Add a historical exchange rate for currency conversion.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>From Currency</Label>
                        <Select
                          value={fxRateForm.from_currency}
                          onValueChange={(v) =>
                            setFxRateForm({ ...fxRateForm, from_currency: v })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>To Currency</Label>
                        <Select
                          value={fxRateForm.to_currency}
                          onValueChange={(v) =>
                            setFxRateForm({ ...fxRateForm, to_currency: v })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Rate</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={fxRateForm.rate}
                        onChange={(e) =>
                          setFxRateForm({ ...fxRateForm, rate: e.target.value })
                        }
                        placeholder="e.g., 0.92"
                        data-testid="fxrate-rate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={fxRateForm.date}
                        onChange={(e) =>
                          setFxRateForm({ ...fxRateForm, date: e.target.value })
                        }
                        data-testid="fxrate-date-input"
                      />
                    </div>
                    <Button
                      onClick={handleCreateFXRate}
                      disabled={saving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="save-fxrate-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Add FX Rate
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fxRates.map((rate) => (
                    <TableRow key={rate.id}>
                      <TableCell>{rate.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{rate.from_currency}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{rate.to_currency}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">{rate.rate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Email/Mailgun Section (Admin Only) */}
        {activeSection === "email" && isAdmin && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Inbound Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Introduction */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div className="text-sm text-emerald-800">
                      <p className="font-medium">Receive attachments via email</p>
                      <p className="mt-1">Send emails with receipts/invoices to your configured address. Attachments will automatically be stored in the Inbox with deduplication.</p>
                    </div>
                  </div>
                </div>

                {/* Webhook URL */}
                <div className="space-y-3">
                  <Label>Webhook URL</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={webhookUrl}
                      readOnly
                      className="font-mono text-sm bg-slate-50"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(webhookUrl)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">This is the URL Mailgun will send inbound emails to.</p>
                </div>

                {/* Setup Instructions */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Mailgun Setup Instructions
                  </h3>
                  
                  <ol className="list-decimal list-inside space-y-3 text-sm text-slate-700">
                    <li>
                      <strong>Create Mailgun Account</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Sign up at{" "}
                        <a href="https://www.mailgun.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-1">
                          mailgun.com <ExternalLink className="w-3 h-3" />
                        </a>
                      </p>
                    </li>
                    <li>
                      <strong>Add Your Domain</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Go to <strong>Sending → Domains</strong> and add your domain (e.g., receipts.yourdomain.com)
                      </p>
                    </li>
                    <li>
                      <strong>Verify DNS Records</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Add the MX records Mailgun provides to your DNS settings
                      </p>
                    </li>
                    <li>
                      <strong>Create Inbound Route</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Go to <strong>Receiving → Routes</strong> and create a new route:
                      </p>
                      <div className="ml-6 mt-2 bg-slate-50 rounded p-3 font-mono text-xs space-y-1">
                        <p><span className="text-slate-500">Expression:</span> match_recipient(&quot;receipts@yourdomain.com&quot;)</p>
                        <p><span className="text-slate-500">Action:</span> forward(&quot;{webhookUrl}&quot;)</p>
                        <p><span className="text-slate-500">Action:</span> store()</p>
                      </div>
                    </li>
                    <li>
                      <strong>Get Webhook Signing Key</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Go to <strong>Settings → Webhooks</strong> and copy your &quot;HTTP webhook signing key&quot;
                      </p>
                    </li>
                    <li>
                      <strong>Configure Environment Variable</strong>
                      <p className="ml-6 text-slate-500 mt-1">
                        Add to your backend environment:
                      </p>
                      <div className="ml-6 mt-2 bg-slate-800 text-emerald-400 rounded p-3 font-mono text-xs">
                        MAILGUN_WEBHOOK_KEY=&quot;your-signing-key-here&quot;
                      </div>
                    </li>
                  </ol>
                </div>

                {/* Current Status */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-3">Current Status</h3>
                  {emailStatus.mailgun_webhook_key_configured ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm text-slate-600">
                        Webhook signature verification is <strong className="text-emerald-600">enabled</strong>.
                        All incoming webhooks will be verified.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                      <span className="text-sm text-slate-600">
                        Webhook signature verification is <strong>disabled</strong> (no MAILGUN_WEBHOOK_KEY configured).
                        The webhook will accept all requests in this mode.
                      </span>
                    </div>
                  )}
                  {emailStatus.emails_received > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                        {emailStatus.emails_received} email(s) received
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Test */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium mb-2">Test Email Reception</h3>
                  <p className="text-sm text-slate-600 mb-3">
                    Once configured, send a test email with an attachment to your configured address.
                    Check the Inbox to verify the attachment was received.
                  </p>
                  <Button variant="outline" asChild>
                    <a href="/inbox">
                      Go to Inbox
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
        )}

        {/* Users Section (Admin only) */}
        {activeSection === "users" && isAdmin && (
            <>
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Users</CardTitle>
                <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New User</DialogTitle>
                      <DialogDescription>
                        Add a new user to the system. They will receive an email with their login credentials.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="user-name">Name</Label>
                        <Input
                          id="user-name"
                          value={userForm.name}
                          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-email">Email</Label>
                        <Input
                          id="user-email"
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-password">Password</Label>
                        <Input
                          id="user-password"
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <Label htmlFor="user-role">Role</Label>
                        <Select
                          value={userForm.role}
                          onValueChange={(role) => setUserForm({ ...userForm, role })}
                        >
                          <SelectTrigger id="user-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="controller">Controller</SelectItem>
                            <SelectItem value="contributor">Contributor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleCreateUser}
                        disabled={saving}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Create User
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Select
                            value={u.role}
                            onValueChange={(role) =>
                              handleUpdateUser(u.id, { role })
                            }
                            disabled={u.id === user.id}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="controller">Controller</SelectItem>
                              <SelectItem value="contributor">Contributor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.active ? "default" : "secondary"}>
                            {u.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {u.id !== user.id && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleOpenEditUser(u)}
                                  data-testid={`edit-user-${u.id}`}
                                  title="Edit user"
                                >
                                  <Edit2 className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleOpenResetPassword(u)}
                                  data-testid={`reset-password-${u.id}`}
                                  title="Reset password"
                                >
                                  <KeyRound className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleOpenDeleteUser(u)}
                                  data-testid={`delete-user-${u.id}`}
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleUpdateUser(u.id, { active: !u.active })
                                  }
                                  data-testid={`toggle-user-${u.id}`}
                                >
                                  {u.active ? "Deactivate" : "Activate"}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Edit User Dialog */}
            <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>
                    Update user details for {editingUser?.email}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="edit-user-name">Name</Label>
                    <Input
                      id="edit-user-name"
                      value={editUserForm.name}
                      onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-user-role">Role</Label>
                    <Select
                      value={editUserForm.role}
                      onValueChange={(role) => setEditUserForm({ ...editUserForm, role })}
                    >
                      <SelectTrigger id="edit-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="controller">Controller</SelectItem>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setEditUserDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEditUser}
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog open={deleteUserDialogOpen} onOpenChange={(open) => {
              if (!open) {
                setDeletingUser(null);
                setDeletingUserAssignments(null);
              }
              setDeleteUserDialogOpen(open);
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete User</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete <strong>{deletingUser?.name}</strong> ({deletingUser?.email})?
                  </DialogDescription>
                </DialogHeader>
                
                {checkingAssignments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    <span className="ml-2 text-sm text-slate-500">Checking assignments...</span>
                  </div>
                ) : deletingUserAssignments?.assigned_transactions > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-amber-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-800">Warning: User has assigned items</p>
                        <p className="text-sm text-amber-700 mt-1">
                          This user has <strong>{deletingUserAssignments.assigned_transactions}</strong> transaction(s) assigned.
                          Deleting will unassign these transactions and mark them as &quot;Previously assigned to {deletingUser?.name}&quot;.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-2">
                    This user has no assigned transactions. The account will be removed from the system.
                  </p>
                )}
                
                <div className="flex gap-2 justify-end mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteUserDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmDeleteUser}
                    disabled={saving || checkingAssignments}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {deletingUserAssignments?.assigned_transactions > 0 ? "Delete Anyway" : "Delete User"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => {
              if (!open) {
                setResetPasswordUser(null);
                setNewPassword("");
              }
              setResetPasswordDialogOpen(open);
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                  <DialogDescription>
                    Set a new password for <strong>{resetPasswordUser?.name}</strong> ({resetPasswordUser?.email})
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password (min 6 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="reset-password-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setResetPasswordDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleResetPassword}
                      disabled={resettingPassword || newPassword.length < 6}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {resettingPassword ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <KeyRound className="w-4 h-4 mr-2" />
                      )}
                      Reset Password
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </>
        )}

        {/* MCP Access Section */}
        {activeSection === "mcp" && isAdmin && <MCPSection />}

        {/* Features Section */}
        {activeSection === "features" && isAdmin && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="w-5 h-5" />
                  Feature Settings
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Enable or disable features across the application
                </p>
              </CardHeader>
              <CardContent>
                {Object.keys(featureFlags).length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Group features by category */}
                    {['storage', 'email', 'transactions', 'analytics'].map(category => {
                      const categoryFeatures = Object.entries(featureFlags).filter(
                        ([_, config]) => config.category === category
                      );
                      
                      if (categoryFeatures.length === 0) return null;
                      
                      const categoryIcons = {
                        storage: <FolderOpen className="w-4 h-4" />,
                        email: <Mail className="w-4 h-4" />,
                        transactions: <Link2 className="w-4 h-4" />,
                        analytics: <BarChart3 className="w-4 h-4" />
                      };
                      
                      const categoryLabels = {
                        storage: 'Inbox',
                        email: 'Email Integration',
                        transactions: 'Transactions',
                        analytics: 'Analytics & Dashboard'
                      };
                      
                      return (
                        <div key={category}>
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                            <span className="text-slate-500">{categoryIcons[category]}</span>
                            <h3 className="font-medium text-slate-700">{categoryLabels[category]}</h3>
                          </div>
                          <div className="space-y-4">
                            {categoryFeatures.map(([key, config]) => (
                              <div 
                                key={key} 
                                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                data-testid={`feature-${key}`}
                              >
                                <div className="flex-1 pr-4">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900">{config.name}</span>
                                    {key === 'filter_non_financial_documents' && (
                                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                                        Affects Storage
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">{config.description}</p>
                                </div>
                                <Switch
                                  checked={config.enabled}
                                  onCheckedChange={() => handleToggleFeature(key, config.enabled)}
                                  data-testid={`toggle-${key}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium">About Feature Flags</p>
                          <p className="mt-1 text-blue-700">
                            Changes take effect immediately. Disabling a feature will not delete any data,
                            it only hides or disables the functionality in the UI.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
        )}

        {/* Currency Conversion Settings Section */}
        {activeSection === "currencies" && isAdmin && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Currency Conversion Settings
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure which currencies should be automatically converted to EUR during document analysis
                  </p>
                </div>
                <Dialog open={currencyDialogOpen} onOpenChange={setCurrencyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-currency-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Currency
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Currency</DialogTitle>
                      <DialogDescription>
                        Add a currency that should be converted to EUR during analysis.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Currency Code</Label>
                        <Input
                          placeholder="e.g., NGN"
                          value={newCurrencyForm.code}
                          onChange={(e) => setNewCurrencyForm({ ...newCurrencyForm, code: e.target.value.toUpperCase() })}
                          maxLength={3}
                          data-testid="currency-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency Name</Label>
                        <Input
                          placeholder="e.g., Nigerian Naira"
                          value={newCurrencyForm.name}
                          onChange={(e) => setNewCurrencyForm({ ...newCurrencyForm, name: e.target.value })}
                          data-testid="currency-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Symbol (optional)</Label>
                        <Input
                          placeholder="e.g., ₦"
                          value={newCurrencyForm.symbol}
                          onChange={(e) => setNewCurrencyForm({ ...newCurrencyForm, symbol: e.target.value })}
                          data-testid="currency-symbol-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCurrencyDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleAddCurrency} disabled={saving} data-testid="save-currency-btn">
                        {saving ? "Saving..." : "Add Currency"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-900">How it works</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        When a document (receipt/invoice) is analyzed and its currency matches an enabled currency below,
                        the system will automatically fetch the historical exchange rate for that date and calculate the EUR equivalent.
                        This helps match invoices against EUR bank transactions accurately.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {conversionCurrencies.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No currencies configured. Add currencies to enable automatic EUR conversion.
                    </div>
                  ) : (
                    conversionCurrencies.map((currency) => (
                      <div
                        key={currency.code}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          currency.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`currency-row-${currency.code}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${
                            currency.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                          }`}>
                            {currency.symbol || currency.code}
                          </div>
                          <div>
                            <div className="font-medium">{currency.code}</div>
                            <div className="text-sm text-slate-500">{currency.name}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm px-2 py-1 rounded ${
                            currency.enabled 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {currency.enabled ? '→ EUR' : 'Disabled'}
                          </span>
                          <Switch
                            checked={currency.enabled}
                            onCheckedChange={() => handleToggleCurrency(currency.code, currency.enabled)}
                            data-testid={`currency-toggle-${currency.code}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => handleRemoveCurrency(currency.code)}
                            data-testid={`currency-remove-${currency.code}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium mb-2">Target Currency</h4>
                  <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-lg">
                    <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-bold">
                      €
                    </div>
                    <div>
                      <div className="font-medium">EUR - Euro</div>
                      <div className="text-sm text-slate-500">All enabled currencies will be converted to EUR</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
        )}

        {/* Danger Zone Section */}
        {activeSection === "danger" && isAdmin && (
          <DangerZoneSection />
        )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};

export default SettingsPage;
